
import BarcodeLabel from '@/components/BarcodeLabel.vue'
import BarcodeKeyValueInfoMixin from '@/components/mixins/BarcodeKeyValueInfoMixin.vue'
import IconDelete from '@/components/svg/IconDelete.vue'
import {
  ADD_TRACKING,
  CLEAR_ERROR,
  FETCH_BARCODE,
  SAVE_SCANNED_BARCODES,
  SET_BULK_ACTIVATION_CONTEXT,
} from '@/store/actions'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { addLabelToBarcode, openMessage } from '@/utils/helpers'
import cloneDeep from 'lodash/cloneDeep'
import { MASKED_ANNOTATION } from 'smartbarcode-web-core/src/utils/constants'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import { IBarcode, IDisplayBarcodes, IProject, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { mixins, Options } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
@Options({
  components: {
    BarcodeLabel,
    IconDelete,
  },
  name: 'ScannedBCList',
})
export default class ScannedBCList extends mixins(BarcodeKeyValueInfoMixin) {
  loading = false
  barcodes = [] as IBarcode[]
  barcodeDialogVisible = false
  barcode = {} as IBarcode
  extParams = {} as Record<string, unknown>
  isAllowToLeave = false
  selectedExternalBarcodeIds: string[] = []

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get isBulkAddTracking() {
    return this.$route.params.operation === ADD_TRACKING
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (this.isAllowToLeave) {
      next()
      return
    }
    await this.$confirm(this.$t('not_save_confirm'), '', {
      confirmButtonText: this.$t('ok'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
    })
      .then(async () => {
        next()
      })
      .catch((e: TError) => {
        if (e !== 'cancel') {
          next()
        }
      })
    next(false)
  }

  async created() {
    try {
      if (!this.$route.params.operation) {
        const parentBarcodeId = this.$store.state?.barcode?.barcode?.id
        if (!parentBarcodeId) {
          this.$router.replace({ name: 'home' })
          return
        }
        this.$router.replace({
          name: 'detail',
          params: {
            project: this.$store.getters.projectParam,
            barcodeId: parentBarcodeId,
          },
        })
      }
      this.loading = this.displayBarcodes.length > 0
      this.getBarcodeInfo()
      this.extParams = this.$route.params.extParams ? JSON.parse(this.$route.params.extParams) : {}

      this.barcodes = [...this.$store.state.barcode.scannedBCPool]
      if (isEmpty(this.barcodes)) this.rescan()
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  get displayBarcodes() {
    const barcodes = cloneDeep(this.barcodes) as IDisplayBarcodes[]
    const currentBC = cloneDeep(this.$store.state.barcode.barcode)
    barcodes.unshift(currentBC)
    addLabelToBarcode(barcodes, this.project)
    return barcodes
  }

  // Reload bc in case user refresh browser
  async getBarcodeInfo() {
    const currentBC = this.$store.state.barcode.barcode
    const urlBCId = this.$route.params?.barcodeId

    if (!currentBC && urlBCId) {
      await this.$store.dispatch(FETCH_BARCODE, { id: this.$route.params?.barcodeId })
    }
  }

  get barcodeName() {
    return this.project?.barcodeTypes?.[this.barcodes[0]?.barcodeType]?.name || ''
  }

  get nextSelectedTrackingPoint(): string {
    return this.extParams?.selectedNextTrackpoint as string
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get currentTrackPointKey() {
    return this.barcodes[0]?.currentTrackPointKey
  }

  get currentTrackPointName(): string {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name || ''
  }

  get title(): string {
    return this.$route.params.title ?? this.$t('scanned_barcode_list')
  }

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  get hasExternalBarcodes() {
    return this.barcodes.some((bc) => bc.isExternal === true)
  }

  get externalBarcodes() {
    return this.barcodes.filter((bc) => bc.isExternal === true)
  }

  get parentBarcode() {
    return this.$store.state.barcode.barcode
  }

  async activateExternalBarcodes() {
    try {
      const externalIds = this.externalBarcodes
        .filter((bc) => this.selectedExternalBarcodeIds.includes(bc.id))
        .map((bc) => bc.externalId)
        .filter(Boolean) as string[]

      if (externalIds.length === 0) {
        openMessage(this.$t('no_external_barcodes_to_activate'), 'warning')
        return
      }

      this.isAllowToLeave = true
      this.$store.dispatch(SET_BULK_ACTIVATION_CONTEXT, {
        externalIds,
        returnRoute: this.$route.name as string,
        operation: this.$route.params.operation as string,
        extParams: this.extParams,
        rescanRoute: 'pair',
      })

      this.$router.push({
        name: 'activate-type',
        params: {
          project: this.$route.params.project,
          barcodeId: this.parentBarcode?.id,
        },
      })
    } catch (error) {
      errorHandler(error as TError)
    }
  }

  onViewDetailItem(bc: IBarcode) {
    this.barcodeDialogVisible = true
    this.barcode = bc
  }

  async onDeleteItem(bcId: string) {
    await this.$confirm(this.$t('confirm_delete_item'), '', {
      confirmButtonText: this.$t('ok'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
    })
      .then(async () => {
        this.selectedExternalBarcodeIds = this.selectedExternalBarcodeIds.filter((id) => id !== bcId)
        this.barcodes = this.barcodes.filter((bc) => bc.id !== bcId)
        this.$store.dispatch(SAVE_SCANNED_BARCODES, [...this.barcodes])
      })
      .catch(() => true)
  }

  rescan() {
    this.isAllowToLeave = true
    const rescanRoute = this.$route.params.rescanRoute
    if (rescanRoute) {
      this.$router.push({
        name: rescanRoute,
        params: {
          project: this.$route.params?.project,
          barcodeId: this.$route.params?.barcodeId,
        },
      })
    } else {
      this.$router.go(-1)
    }
  }

  submit() {
    this.loading = true
    this.isAllowToLeave = true
    this.$store.dispatch(this.$route.params.operation, this.extParams)
  }

  get isAllowForcePairing() {
    return this.$store.state.project?.details?.allowForcePairing || false
  }

  @Watch('$store.state.barcode.error')
  operationFailed() {
    this.loading = false
    if (!this.$store.state.barcode.error) return

    const err = this.$store.state.barcode.errorMsg
    if (err === '3044' && this.isAllowForcePairing) {
      this.$confirm('', this.$t('confirm_overwrite_parent'), {
        confirmButtonText: this.$t('ok'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('cancel'),
      })
        .then(async () => {
          this.extParams.forcePairing = true
          this.forward()
        })
        .catch(() => true)
        .finally(() => this.$store.commit(CLEAR_ERROR))
      return
    }
    const modelObject: Record<string, string> = {
      barcodeType: this.barcodeName,
      fTP1: this.currentTrackPointName,
      tTP2: this.nextSelectedTrackingPoint,
    }

    errorHandler(this.$store.state.barcode.errorMsg, modelObject)
    this.$store.commit(CLEAR_ERROR)
  }

  @Watch('$store.state.barcode.actionCompleted')
  operationCompleted() {
    this.loading = false
    if (this.isBulkAddTracking) {
      localStorage.removeItem(LOCAL_STORAGE_ITEM.SCAN_TIMESTAMP)
    }
    const parentBarcodeId = this.$store.state.barcode.barcode.id
    this.$router.replace({
      name: 'detail',
      params: {
        project: this.$store.getters.projectParam,
        barcodeId: parentBarcodeId,
      },
    })
  }
}
